import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useDeferredValue, useMemo, useState } from 'react';

import { useEvent } from '@mint-lib/hooks';
import { typographyVariants } from '@mint-lib/tw-components';
import {
  ComboboxInput,
  ComboboxInputLabel,
  ComboboxInputRoot,
  ComboboxMenu,
  ComboboxMenuButton,
} from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';

import { OptionRow } from './OptionRow.jsx';

export type MultiSelectOption = { label: string; value: string };

type TextLabels = {
  search: string;
  selectAll: string;
  clearAll: string;
  empty: string;
  loading: string;
};

type Props = {
  id: string;
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
  textLabels: TextLabels;
  label?: string;
  groupLabel?: string;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
};

const ROW_HEIGHT_PX = 40;
const OVERSCAN_COUNT = 5;

const VirtualizedMultiSelect = ({
  id,
  options,
  value,
  onChange,
  placeholder,
  textLabels,
  label,
  groupLabel,
  disabled,
  isLoading,
  className,
}: Props) => {
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null,
  );
  const [search, setSearch] = useState('');

  const deferredQuery = useDeferredValue(search.trim().toLowerCase());

  const visibleOptions = useMemo(
    () =>
      deferredQuery
        ? options.filter((option) =>
            option.label.toLowerCase().includes(deferredQuery),
          )
        : options,
    [options, deferredQuery],
  );

  const selected = useMemo(() => new Set(value), [value]);

  const displayValue = useMemo(
    () =>
      options
        .filter((option) => selected.has(option.value))
        .map((option) => option.label)
        .join(', '),
    [options, selected],
  );

  const listRef = useCallback((node: HTMLDivElement | null) => {
    setScrollElement(
      node?.closest<HTMLDivElement>('[data-slot="command-list"]') ?? null,
    );
  }, []);

  const rowVirtualizer = useVirtualizer({
    count: visibleOptions.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => ROW_HEIGHT_PX,
    overscan: OVERSCAN_COUNT,
  });

  const handleToggle = useEvent((optionValue: string) => {
    const nextValue = selected.has(optionValue)
      ? value.filter((item) => item !== optionValue)
      : [...value, optionValue];

    onChange(nextValue);
  });

  const handleSelectAll = useEvent((event?: React.MouseEvent) => {
    event?.preventDefault();
    onChange(options.map((option) => option.value));
  });

  const handleClearAll = useEvent((event?: React.MouseEvent) => {
    event?.preventDefault();
    onChange([]);
  });

  const handleOpenChange = (open: boolean) => {
    if (!open) setSearch('');
  };

  return (
    <ComboboxInputRoot className={className}>
      {label && (
        <ComboboxInputLabel htmlFor={id} label={label} disabled={disabled} />
      )}
      <ComboboxInput
        popoverClassName="z-popover"
        inputProps={{ id, autoComplete: 'off' }}
        size="medium"
        value={displayValue}
        placeholder={placeholder}
        onOpenChange={handleOpenChange}
        disabled={disabled}
      >
        <ComboboxMenu
          withSearch
          shouldFilter={false}
          placeholder={textLabels.search}
          onValueChange={setSearch}
          emptyMessage={isLoading ? textLabels.loading : textLabels.empty}
        >
          {options.length > 0 && (
            <ComboboxMenuButton
              selectedOptions={value}
              options={options}
              onSelectAll={handleSelectAll}
              onClearAll={handleClearAll}
              selectAllText={textLabels.selectAll}
              clearAllText={textLabels.clearAll}
            />
          )}

          {groupLabel && visibleOptions.length > 0 && (
            <p
              className={cn(
                'px-3 pt-4 pb-1',
                typographyVariants({
                  variant: 'overline1',
                  color: 'secondary',
                }),
              )}
            >
              {groupLabel}
            </p>
          )}

          <div
            ref={listRef}
            className="relative w-full"
            style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualItem) => {
              const option = visibleOptions[virtualItem.index];

              return (
                <OptionRow
                  key={option.value}
                  value={option.value}
                  label={option.label}
                  selected={selected.has(option.value)}
                  size={virtualItem.size}
                  start={virtualItem.start}
                  onToggle={handleToggle}
                />
              );
            })}
          </div>
        </ComboboxMenu>
      </ComboboxInput>
    </ComboboxInputRoot>
  );
};

export { VirtualizedMultiSelect };
